import React, { useEffect, useState } from "react";
import { api } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Info, AlertTriangle, XCircle, CheckCircle, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";

function TypeIcon({ type }) {
  switch (type) {
    case "SUCCESS":
      return <CheckCircle className="w-5 h-5 text-green-600" />;
    case "WARNING":
      return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
    case "ERROR":
      return <XCircle className="w-5 h-5 text-red-600" />;
    case "ACTION_REQUIRED":
      return <AlertTriangle className="w-5 h-5 text-orange-600" />;
    default:
      return <Info className="w-5 h-5 text-muted-foreground" />;
  }
}

const NotificationsAdminSummary = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [active, setActive] = useState([]);
  const [archived, setArchived] = useState([]);
  const [detail, setDetail] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!token) return;
    loadData();
  }, [token]);

  const loadData = async () => {
    try {
      const [a, ar] = await Promise.all([
        api.notificationAdminAll(),
        api.notificationsAdminArchived(5, 0),
      ]);
      setActive(Array.isArray(a) ? a : []);
      setArchived(Array.isArray(ar?.items) ? ar.items : []);
    } catch (e) {
      console.error("Admin notifications load error", e);
    }
  };

  const openDetail = async (id) => {
    try {
      const n = await api.notificationGet(id);
      setDetail(n);
      setOpen(true);
    } catch (e) {
      console.error("Load detail failed", e);
    }
  };

  const Item = ({ n }) => (
    <button
      onClick={() => openDetail(n.id)}
      className="w-full text-left p-3 border rounded hover:bg-muted transition flex gap-3"
    >
      <div className="mt-0.5">
        <TypeIcon type={n.type} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="font-medium text-sm text-foreground line-clamp-1">{n.title}</div>
          <div className="text-xs text-muted-foreground">{new Date(n.createdAt).toLocaleString("de-DE")}</div>
        </div>
        <div className="text-sm text-muted-foreground line-clamp-2">{n.message}</div>
        {n.actionUrl && n.actionLabel && (
          <div className="mt-1">
            <a
              href={n.actionUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1"
            >
              {n.actionLabel}
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}
      </div>
    </button>
  );

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <Card className="p-4">
        <div className="font-semibold mb-3">Aktuelle Nachrichten</div>
        <div className="space-y-2">
          {active.slice(0, 10).map((n) => (
            <Item key={n.id} n={n} />
          ))}
          {!active.length && (
            <div className="text-sm text-muted-foreground">{t('system.common.none')}</div>
          )}
        </div>
      </Card>
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold">Archiv (letzte 5)</div>
          <Button size="sm" variant="outline" onClick={() => navigate("/admin/notifications")}>Mehr</Button>
        </div>
        <div className="space-y-2">
          {archived.map((n) => (
            <Item key={n.id} n={n} />
          ))}
          {!archived.length && (
            <div className="text-sm text-muted-foreground">{t('notifications.archive.none')}</div>
          )}
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          {detail ? (
            <>
              <DialogHeader>
                <DialogTitle>{detail.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="text-xs text-muted-foreground">
                  {new Date(detail.createdAt).toLocaleString("de-DE")} – Absender: {detail.senderName}
                </div>
                <div className="whitespace-pre-wrap text-sm text-foreground">{detail.message}</div>
                {detail.actionUrl && detail.actionLabel && (
                  <div>
                    <a
                      href={detail.actionUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1"
                    >
                      {detail.actionLabel}
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-sm text-muted-foreground">Lade…</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationsAdminSummary;
